import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Organization } from '@pigello/pigello-matrix';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useQueryState } from 'nuqs';
import { useSession } from '@/providers/session-provider';
import { useGetOrganization } from '@/config/sales/organization/client';
import { fetchAllInstances } from '@/requests/api/fetch-all-instances';
import { setSessionValues } from '@/providers/session-provider/set-session-values';
import { USER_QUERY_KEY } from '@/providers/session-provider/use-current-user';
import { Icons } from '@/components/icons';

export function OrganizationSelector() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useQueryState('filter');
  const {
    data: { session, user },
  } = useSession();
  const currOrgId = session.organizationId;
  const { data, isPending: isLoadingOrganizations } = useQuery({
    queryKey: ['organization', 'picker', user.organization.id],
    queryFn: () =>
      fetchAllInstances<Organization>({
        modelName: 'organization',
        filters: {
          id: {
            '__in!': user.organization.id,
          },
        },
      }),
  });

  const hasMoreThanOneOrganization =
    data && data.list.length > 0 && !isLoadingOrganizations;

  const { data: mainOrg, isLoading: isLoadingMainOrg } = useGetOrganization({
    id: user.organization.id,
    enabled: hasMoreThanOneOrganization,
  });

  const currentOrg = data?.list.find((org) => org?.id === currOrgId) || mainOrg;

  const chooseOrg = (orgId: string) => {
    setOpen(false);
    if (orgId === currOrgId) {
      return;
    }
    if (filters) {
      setFilters(null);
    }
    setSessionValues({ organizationId: orgId });
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.at(0) !== USER_QUERY_KEY;
      },
    });
    queryClient.invalidateQueries();
  };

  const isLoading = isLoadingOrganizations || isLoadingMainOrg;

  if (!hasMoreThanOneOrganization && !isLoading) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label='Välj organisation'
        className='flex h-8 w-full items-center justify-between gap-2 overflow-hidden rounded-input border border-input px-4 py-1 text-sm'
      >
        <div className='min-w-0 flex-1 truncate text-left'>
          {isLoading ? 'Laddar...' : currentOrg?.name}
        </div>
        <Icons.selectIndicator className='size-4 shrink-0' />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 overflow-hidden p-0'>
        <Command label='Välj organisation'>
          <CommandInput
            placeholder='Sök organisation...'
            aria-label='Sök organisation'
            isLoading={isLoading}
          />
          <CommandList className='max-h-[min(300px,calc(var(--radix-popover-content-available-height)-3rem))] overscroll-contain'>
            {isLoading ? (
              <div className='p-3 text-sm text-muted-foreground'>
                Laddar organisationer...
              </div>
            ) : (
              <>
                <CommandEmpty>Inga organisationer hittades.</CommandEmpty>
                <CommandGroup heading='Välj organisation'>
                  <CommandItem
                    value={user.organization.id}
                    keywords={[mainOrg?.name ?? '', 'Huvudorganisation']}
                    onSelect={() => chooseOrg(user.organization.id)}
                    className='gap-2'
                  >
                    <Icons.check
                      className={`size-4 shrink-0 ${currOrgId === user.organization.id ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <div className='min-w-0'>
                      <div className='font-medium break-words'>
                        {mainOrg?.name}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        Huvudorganisation
                      </div>
                    </div>
                  </CommandItem>
                  {data?.list.map((org) => (
                    <CommandItem
                      key={org.id}
                      value={org.id}
                      keywords={[org.name]}
                      onSelect={() => chooseOrg(org.id)}
                      className='gap-2'
                    >
                      <Icons.check
                        className={`size-4 shrink-0 ${org.id === currOrgId ? 'opacity-100' : 'opacity-0'}`}
                      />
                      <span className='min-w-0 break-words'>{org.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
